import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { setNlMessage } from 'store/nl/actions';
import AutocompleteDropdown, { ICON_MAP } from './autocomplete-dropdown';
import ContextChipBar from './context-chip-bar';
import styles from './command-bar.module.css';

const contextKey = (ctx) => ctx.id || `region-${ctx.metroRegion}`;

const CHIP_COLORS = {
  person: '#38b2d9',
  org: '#97b912',
  fund: '#deaf00',
  education: '#ff6624',
  engagement: '#d84884',
  region: '#10b981',
};

const CommandBar = ({ onClose }) => {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [selectedContexts, setSelectedContexts] = useState([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [atPosition, setAtPosition] = useState(null);

  // Focus input on mount
  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  // Track selected context IDs for filtering
  const selectedContextIds = useMemo(
    () => new Set(selectedContexts.map(contextKey)),
    [selectedContexts],
  );

  const buildContextObject = useCallback((item) => {
    if (item.type === 'region' || item.label === 'region') {
      return {
        type: 'region',
        label: 'region',
        id: '',
        metroRegion: item.metroRegion || item.value,
        value: item.displayValue || item.metroRegion || item.value,
      };
    }
    const display = item.displayValue || item.name || item.value || item.id;
    return {
      type: 'vertex',
      label: item.label || 'person',
      id: item.id,
      metroRegion: item.metroRegion,
      displayValue: display,
      value: display,
    };
  }, []);

  const handleSelect = useCallback(
    (item) => {
      const ctx = buildContextObject(item);
      setSelectedContexts((prev) => [...prev, ctx]);

      // Replace the @searchTerm in the contentEditable with a pill
      if (inputRef.current && atPosition !== null) {
        const el = inputRef.current;

        const textContent = el.innerText || '';
        const before = textContent.slice(0, atPosition);
        const after = textContent.slice(atPosition + 1 + searchTerm.length);

        const ctxKey = contextKey(ctx);
        const bgColor = CHIP_COLORS[ctx.label] || CHIP_COLORS.person;
        const textColor = ctx.label === 'fund' ? '#333' : '#fff';
        const icon = ICON_MAP[ctx.label] || '\u{1F464}';

        const pillDisplay = ctx.displayValue || ctx.value;
        el.innerHTML =
          before +
          `\u200B<span data-context-id="${ctxKey}" contentEditable="false" class="${styles.contextPill}" style="background:${bgColor};color:${textColor}">${icon} ${pillDisplay}</span>\u200B` +
          after;

        // Move cursor after the pill
        const sel = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      }

      setShowAutocomplete(false);
      setSearchTerm('');
      setAtPosition(null);
      inputRef.current?.focus();
    },
    [buildContextObject, atPosition, searchTerm],
  );

  const handleRemoveContext = useCallback((ctx) => {
    const ctxKey = contextKey(ctx);
    setSelectedContexts((prev) =>
      prev.filter((c) => contextKey(c) !== ctxKey),
    );

    // Remove the pill from the contentEditable
    if (inputRef.current) {
      const pill = inputRef.current.querySelector(
        `[data-context-id="${ctxKey}"]`,
      );
      if (pill) pill.remove();
    }
  }, []);

  const handleInput = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;

    const text = el.innerText || '';

    // Find the last @ in plain text (outside pills)
    const lastAt = text.lastIndexOf('@');
    if (lastAt !== -1) {
      setAtPosition(lastAt);
      const term = text.slice(lastAt + 1).replace(/\u200B/g, '').trim();
      setSearchTerm(term);
      setShowAutocomplete(true);
    } else {
      setShowAutocomplete(false);
      setSearchTerm('');
      setAtPosition(null);
    }

    // Sync chip bar with remaining pills
    const pillNodes = el.querySelectorAll('[data-context-id]');
    const pillIds = new Set(
      Array.from(pillNodes).map((p) => p.dataset.contextId),
    );
    setSelectedContexts((prev) =>
      prev.filter((ctx) => pillIds.has(contextKey(ctx))),
    );
  }, []);

  const handleSubmit = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;

    // Build processed message: replace pills with their IDs/metroRegions
    const clone = el.cloneNode(true);
    const pills = clone.querySelectorAll('[data-context-id]');
    pills.forEach((pill) => {
      const ctxId = pill.dataset.contextId;
      const ctx = selectedContexts.find(
        (c) => contextKey(c) === ctxId,
      );
      if (ctx) {
        pill.replaceWith(` ${ctx.id || ctx.metroRegion} `);
      }
    });

    const processedMessage = clone.textContent
      .replace(/\u200B/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (!processedMessage) return;

    dispatch(setNlMessage(processedMessage, selectedContexts));
    el.innerHTML = '';
    setSelectedContexts([]);
    setShowAutocomplete(false);
    setSearchTerm('');
    onClose();
  }, [dispatch, selectedContexts, onClose]);

  const handleKeyDown = useCallback(
    (e) => {
      // Let autocomplete handle arrow/enter if open
      if (showAutocomplete && autocompleteRef.current) {
        const handled = autocompleteRef.current.handleKeyDown?.(e);
        if (handled) return;
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        if (showAutocomplete) {
          setShowAutocomplete(false);
          setSearchTerm('');
        } else {
          onClose();
        }
        return;
      }

      if (e.key === 'Enter' && !e.shiftKey && !showAutocomplete) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [showAutocomplete, onClose, handleSubmit],
  );

  return (
    <div className={styles.commandBarWrapper}>
      <div className={styles.gradientBackdrop} />
      <div className={styles.inputContainer}>
        {/* Autocomplete dropdown — above input */}
        {showAutocomplete && (
          <AutocompleteDropdown
            ref={autocompleteRef}
            searchTerm={searchTerm}
            onSelect={handleSelect}
            selectedContextIds={selectedContextIds}
          />
        )}

        {/* Context chips */}
        <ContextChipBar
          contexts={selectedContexts}
          onRemove={handleRemoveContext}
        />

        {/* Input bar */}
        <div className={styles.inputBar}>
          <div
            ref={inputRef}
            contentEditable
            className={styles.input}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            data-placeholder="Ask about your graph... Type @ to add context"
            role="textbox"
            aria-label="AI command bar input"
          />
          <button
            className={styles.sendButton}
            onClick={handleSubmit}
            aria-label="Send query"
          >
            ↑
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommandBar;
